/**
 * LevelUpFooter — shared two-row footer matching the LevelUp brand bar.
 * Mirrors the footer style from admin-fixes-preview-1.emergent.host (minus FAQ/Cert FAQ).
 * Links: LevelUp Home | Support | Terms of Use | Privacy Statement | Trust Center
 * Logo click → navigates to app home ("/") or skillupwithlevelup.com on login page.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';

// Microsoft 4-square logo (inline, no external dep)
function MsLogo({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 21 21" style={{ flexShrink: 0 }}>
      <rect x="0"  y="0"  width="10" height="10" fill="#f25022" />
      <rect x="11" y="0"  width="10" height="10" fill="#7fba00" />
      <rect x="0"  y="11" width="10" height="10" fill="#00a4ef" />
      <rect x="11" y="11" width="10" height="10" fill="#ffb900" />
    </svg>
  );
}

const NAV_LINKS = [
  { label: 'LevelUp Home',      href: 'https://skillupwithlevelup.com', external: true },
  { label: 'Support',           href: 'https://skillupwithlevelup.com/contact-us', external: true },
  { label: 'Terms of Use',      href: 'https://skillupwithlevelup.com/terms-of-use', external: true },
  { label: 'Privacy Statement', href: 'https://technofocus.ai/privacy', external: true },
  { label: 'Trust Center',      href: 'https://technofocus.ai/trust', external: true },
];

const BG = '#091F2C';

export function LevelUpFooter() {
  const year = new Date().getFullYear();
  const navigate = useNavigate();
  const goHome = () => navigate('/');

  return (
    <footer
      data-testid="levelup-footer"
      style={{ background: BG, borderTop: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}
    >
      {/* ── Row 1: brand + nav links ────────────────────────────────── */}
      <div
        style={{
          maxWidth: 1440,
          margin: '0 auto',
          padding: '16px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 24,
          flexWrap: 'wrap',
        }}
      >
        {/* Left: Technofocus | LevelUp + Sponsored by — clickable → home */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <button
            onClick={goHome}
            aria-label="Go to dashboard"
            style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            <img
              src="/technofocus-white.png"
              alt="Technofocus"
              style={{ height: 18, filter: 'brightness(0) invert(1)', objectFit: 'contain' }}
            />
            <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 16, fontWeight: 300 }}>|</span>
            <img
              src="/logo-levelup.png"
              alt="LevelUp"
              style={{ height: 18, filter: 'brightness(0) invert(1)', objectFit: 'contain' }}
            />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: 'rgba(185,220,210,0.55)', fontSize: 12 }}>Sponsored by</span>
            <MsLogo size={14} />
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: 500 }}>Microsoft</span>
          </div>
        </div>

        {/* Right: nav links */}
        <nav style={{ display: 'flex', alignItems: 'center', gap: 0, flexWrap: 'wrap' }}>
          {NAV_LINKS.map((link, i) => (
            <React.Fragment key={link.label}>
              {i > 0 && (
                <span style={{ color: 'rgba(255,255,255,0.15)', padding: '0 10px', fontSize: 13 }}>|</span>
              )}
              <a
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: 'rgba(185,220,210,0.7)',
                  fontSize: 12,
                  fontWeight: 500,
                  textDecoration: 'none',
                  transition: 'color 0.15s',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={e => { e.currentTarget.style.color = '#D4EC8E'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'rgba(185,220,210,0.7)'; }}
              >
                {link.label}
              </a>
            </React.Fragment>
          ))}
        </nav>
      </div>

      {/* ── Row 2: copyright + sponsored ────────────────────────────── */}
      <div
        style={{
          borderTop: '1px solid rgba(255,255,255,0.06)',
          maxWidth: 1440,
          margin: '0 auto',
          padding: '10px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ color: 'rgba(185,220,210,0.4)', fontSize: 11 }}>
          © {year} Technofocus. All rights reserved.
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: 'rgba(185,220,210,0.35)', fontSize: 11 }}>Sponsored by</span>
          <MsLogo size={12} />
          <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, fontWeight: 500 }}>Microsoft</span>
        </div>
      </div>
    </footer>
  );
}
